import { cloneElement, createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type HTMLAttributes, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { lessonTranslationSource, type LessonTranslations } from "../../shared/lesson-translation";
import type { Lesson } from "../../shared/types";
import type { ApiClient } from "./api";

const TranslationContext = createContext<{ translations: Record<string, string>; message: string } | null>(null);
export const KOREAN_TRANSLATION_STORAGE_KEY = "jireh-lingo-korean-translation";

export function readKoreanTranslationEnabled(): boolean {
  try { return localStorage.getItem(KOREAN_TRANSLATION_STORAGE_KEY) !== "off"; } catch { return true; }
}

export function SentenceTranslationProvider({ api, enabled, lesson, children }: {
  api: ApiClient; enabled: boolean; lesson: Lesson | null; children: ReactNode;
}) {
  const source = lesson ? lessonTranslationSource(lesson) : "";
  const lessonId = lesson?.id;
  const [result, setResult] = useState<LessonTranslations | null>(null);
  const [failure, setFailure] = useState("");
  const requests = useRef(new Map<string, Promise<LessonTranslations>>());
  useEffect(() => {
    if (!enabled || !lessonId) return;
    let cancelled = false;
    setFailure("");
    const key = `${lessonId}:${source}`;
    let request = requests.current.get(key);
    if (!request) {
      request = api.getLessonTranslations(lessonId);
      requests.current.set(key, request);
    }
    void request.then((value) => {
      if (!cancelled) {
        if (value.source !== source) throw new Error("Lesson changed");
        setResult(value);
      }
    }).catch(() => {
      requests.current.delete(key);
      if (!cancelled) setFailure("번역을 불러오지 못했습니다. 한국어 번역을 껐다 켜서 다시 시도해 주세요.");
    });
    return () => { cancelled = true; };
  }, [api, enabled, lessonId, source]);
  const value = enabled && lessonId ? {
    translations: result?.source === source ? result.translations : {},
    message: failure || "한국어 번역을 준비하고 있습니다…"
  } : null;
  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
}

export function SentenceTranslation({ segmentId, children }: { segmentId: string; children: ReactElement<HTMLAttributes<HTMLElement>> }) {
  const context = useContext(TranslationContext);
  const id = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  const tooltip = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0, arrow: 24, below: false });
  const text = context?.translations[segmentId] ?? context?.message ?? "";
  const visible = Boolean(context && open);
  const cancelClose = () => { if (timer.current) clearTimeout(timer.current); };
  const close = () => { cancelClose(); timer.current = setTimeout(() => setOpen(false), 120); };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useEffect(() => { setOpen(false); }, [context === null, segmentId]);
  useLayoutEffect(() => {
    if (!visible || !anchor.current || !tooltip.current) return;
    const target = anchor.current.firstElementChild ?? anchor.current;
    const rect = target.getBoundingClientRect();
    const box = tooltip.current.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    const left = Math.max(12, Math.min(center - box.width / 2, window.innerWidth - box.width - 12));
    const below = rect.top < box.height + 22;
    setPosition({ left, top: below ? rect.bottom + 10 : rect.top - box.height - 10, arrow: Math.max(16, Math.min(center - left, box.width - 16)), below });
    const dismiss = () => setOpen(false);
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") dismiss(); };
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("keydown", onKey);
    };
  }, [visible, text]);
  return <span ref={anchor} className="sentence-translation" onMouseEnter={() => { cancelClose(); setOpen(true); }} onMouseLeave={close} onFocus={() => { cancelClose(); setOpen(true); }} onBlur={close}>
    {cloneElement(children, { "aria-describedby": visible ? id : undefined })}
    {visible && createPortal(<span ref={tooltip} id={id} role="tooltip" lang="ko" className={`sentence-translation-tooltip${position.below ? " below" : ""}`} style={{ left: position.left, top: position.top }} onMouseEnter={cancelClose} onMouseLeave={close}>
      <span className="translation-language">한국어</span>{text}<span className="translation-arrow" style={{ left: position.arrow }} aria-hidden="true" />
    </span>, document.body)}
  </span>;
}
